import React, { useEffect, useRef, useState } from 'react'
import { CampaignDetails, PaywallTranslations } from './paywall-comp';
import { BrazeAttributes } from '../../../../../javascript/interface';
import { signIn, signUp, validateEmail } from '../../../../javascript/account-api';
import { ReadlyLoading } from '../../../react/vectors/readly_loading';
import style from './paywall-inline-signup.module.scss';
import { Typography } from '../../../react/components/typography/typography';
import InputText from '../../../react/components/input-text/input-text';
import { CoverScrollerAndDevices } from './v1/cover-scroller-and-devices';
import Select from '../../../react/components/select/select';
import { ChevronUp } from '../../../react/vectors/chevron_up';
import { CountrySelectorOption } from './paywall';

export default function PaywallInlineSignup ({
  translations,
  onRegister,
  onSignin,
  onCountryChange,
  freeTrialTag,
  freeTrialCampaignToken,
  currentCountry,
  countrySelectOptions,
  topPublications,
  onValidEmailEntered,
  onCreatePasswordShown,
  onEmailExists,
  articleDistributions,
  onSignInAccountExistsInDifferentCountryWithArticleAvailable,
} : {
  campaignDetails: CampaignDetails
  translations: PaywallTranslations
  onRegister: (brazeAttributes:BrazeAttributes, tracking: Object) => void
  onSignin: (account: any, tracking: any) => void
  onCountryChange: (country: string) => void;
  freeTrialTag: string,
  freeTrialCampaignToken: string;
  currentCountry: string;
  countrySelectOptions: CountrySelectorOption[];
  topPublications: [],
  onValidEmailEntered: () => void,
  onCreatePasswordShown: () => void,
  onEmailExists: () => void,
  articleDistributions: string[],
  onSignInAccountExistsInDifferentCountryWithArticleAvailable: (account_country: string) => void,
}) {
  const url = new URL(window.location.href);

  const [email, setEmail] = useState<string>(url.searchParams.get('email') || "");
  const [password, setPassword] = useState<string>("");
  const [accountExists, _] = useState<boolean>(url.searchParams.get('accountExists') == "1")
  const [country, setCountry] = useState<string>(currentCountry);
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [formState, setFormState] = useState<'signin' | 'signup' | null>(null);

  const signUpBtnRef = useRef(null)
  const [buttonHasBeenVisibleToUser, setButtonHasBeenVisibleToUser] = useState(false);

  useEffect(() => {
    if (email.length > 0) {
      setEmail(email);
      handleValidateEmail(email);
    }
  }, [])
  

  useEffect(() => {
    const callback: IntersectionObserverCallback = (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !buttonHasBeenVisibleToUser) {
          setButtonHasBeenVisibleToUser(true);
          // onPaywallButtonVisibleForUser()
        }
      }
    }
    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px',
      threshold: 1
    }

    const observer = new IntersectionObserver(callback, options)
    if (signUpBtnRef.current) {
      observer.observe(signUpBtnRef.current)
    }
  
    return () => {
      observer.disconnect()
    }
  }, [signUpBtnRef, buttonHasBeenVisibleToUser])

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (formState == 'signup') {
      handleSignUpSubmit(e);
    } else if (formState == 'signin') {
      handleSignInSubmit(e);
    } else {
      handleValidateEmail(e);
    }
  }

  const handleSignUpSubmit = (e) => {
    setInProgress(true);
    setFormErrors([]);

    signUp(email.trim(), password, freeTrialTag, freeTrialCampaignToken, country, (success, errors, response) => {
      if (!success) {
        setFormErrors(errors)
        setInProgress(false)
      } else {
        onRegister(response.brazeAttributes, response.tracking);
      }
    })
  }

  const handleSignInSubmit = (e) => {
    setInProgress(true);
    setFormErrors([]);

    signIn(email.trim(), password, (success, info, account, tracking) => {
      if (success) {
        onSignin(account, tracking);
      } else {
        setFormErrors([info]);
        setInProgress(false);
        const accountCountry = account?.readly_country
        if (accountCountry && accountCountry != currentCountry) {
          if (articleDistributions.includes(accountCountry)) {
            redirectToCountry(accountCountry, true);
          } else {
            const temp = `${location.origin}`.replace(/(:\/\/\w+\.)/, `://${accountCountry}.`)
            const url = new URL(temp)
            url.pathname = '/accounts/login'
            url.searchParams.set('email', email);
            url.searchParams.set('global_account', 'true');
            window.location.href = url.href;
          }
        }
      }
    })
  }

  const handleValidateEmail = (e) => {
    setFormErrors([])

    const setErrors = () => {
      setFormErrors([translations['lokalise.validations.failed.email']])
    }

    if (email.trim() == '') {
      setErrors();
      return;
    }

    validateEmail(email.trim(), (result) => {
      if (!result.valid) {
        setErrors();
        return;
      }

      onValidEmailEntered();

      if (result.exists) {
        setFormState('signin');
        onEmailExists();
        onSignInAccountExistsInDifferentCountryWithArticleAvailable(result.exists_in_country);
      } else {
        setFormState('signup')
        onCreatePasswordShown();
      }
    })
  }

  const redirectToCountry = (country, accountExists) => {
    const temp = `${location.href}`.replace(/(:\/\/\w+\.)/, `://${country}.`);
    const url = new URL(temp)
    url.searchParams.set('email', email);
    url.searchParams.set('fromCountry', currentCountry);
    if (accountExists) {
      url.searchParams.set('accountExists', "1");
    }
    if (url.pathname.includes("/international/")) {
      window.location.href = url.href;
    } else {
      window.location.href = `${url.origin}/international/${currentCountry.toLowerCase()}${url.pathname}${url.search}`;
    }
  }

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setCountry(newCountry)
    onCountryChange(newCountry);

    redirectToCountry(newCountry, false)
  }

  const title = (() => {
    if (formState == null || formState == 'signup') {
      return translations['lokalise.catalog.article_paywall.sign_up_view_title'];
    } else if (formState == 'signin') {
      return translations['lokalise.welcome_back'];
    }
  })();

  const signUpButtonText = (() => {
    if (inProgress) {
      return <ReadlyLoading className='readly-loading' />
    } else if (formState == 'signin') {
      return <>{translations['lokalise.sign_in']}</>;
    } else if (formState == 'signup') {
      return <>{translations['lokalise.create_account']}</>;
    } else {
      return <>{translations['lokalise.catalog.article_paywall.continue_reading']}</>;
    }
  })();

  return (
    <div style={{width: '100%', padding: '32px 0'}}>
      <div style={{margin: '0 auto', maxWidth: '462px', width: '100%', padding: '0 16px'}}>
        <Typography
          element={'h4'}
          themeColor={'text-ink-main'}
          textSize={'sys-display-small'}
          className={style.title}
          style={{textAlign: 'center'}}
        >
          {title}
        </Typography>

        <Typography
          element={'p'}
          themeColor={'text-ink-offset'}
          textSize={'sys-title-medium'}
          className={style.title}
          style={{textAlign: 'center', marginTop: '8px'}}
        >

          {accountExists ?
            translations['descriptions.global_account']
            :
            translations['lokalise.catalog.article_paywall.read_gives']}
        </Typography>


        <div>
          <>
            <form onSubmit={handleFormSubmit}>
              <div style={{marginTop: '32px'}}>
                <InputText
                  type='text'
                  placeholder={translations['dictionary.email_address']}
                  onChange={(e, value) => {
                    setFormState(null);
                    setFormErrors([]);
                    setEmail(value);
                  }}
                  value={email}
                  onBlur={(_e, email) => handleValidateEmail(email)}
                />
              </div>

              <div style={{marginTop: '16px', display: formState ? 'block' : 'none'}}>
                <InputText
                  type='password'
                  placeholder={translations['dictionary.password']}
                  onChange={(e, value) => setPassword(value)}
                />
              </div>

              {formState == 'signup' ?
              <div style={{marginTop: '16px'}}>
                <Select
                  text={translations['lokalise.catalog.article_paywall.where_live']}
                  onChange={handleCountryChange}
                  value={country}
                  trailingIcon={<ChevronUp className={style.countrySelectorChevron} />}
                  options={countrySelectOptions}
                />
              </div>
              :null}

            {formErrors.map((error, idx) =>
              <div key={idx} className="sys-body-small" style={{marginTop: '8px', color: "#D73737"}}>
                {error}
              </div>
            )}

            <Typography
              element={'p'}
              themeColor={'text-ink-offset'}
              textSize={'sys-title-medium'}
              className={style.title}
              style={{marginTop: '16px'}}
            >
              {translations['lokalise.catalog.article_paywall.limited_regions']}
            </Typography>

              <button
                type="submit"
                className={`primary-button primary-button-large primary-button-large-light`}
                style={{marginTop: '16px', width: '100%', whiteSpace: 'normal'}}
                disabled={inProgress}
                ref={signUpBtnRef}
              >
                {signUpButtonText}
              </button>
            </form>

            <Typography
              element={'p'}
              themeColor={'text-ink-offset'}
              textSize={'sys-body-small'}
              className={style.title}
              style={{marginTop: '8px'}}
              dangerouslySetInnerHTML={{__html: translations['lokalise.catalog.article_paywall.tos_text']}}
            />
          </>
        </div>
      </div>
      <div style={{maxWidth: '700px', margin: '0 auto'}}>
        <CoverScrollerAndDevices
          translations={translations}
          topPublications={topPublications}
        />
      </div>
    </div>
  );
}
